<script setup lang="ts">
import { useDialogPluginComponent } from 'quasar';
import { copyToClipboardWithNotification } from '@/helpers/clipboardUtils.ts';
import FileViewer from '@/components/FileViewer.vue';
import { useFilesActions } from '@/composables/useFilesActions.ts';
import useNavigateFolder from '@/components/TableTransactions/useNavigateFolder.ts';

defineEmits([...useDialogPluginComponent.emits]);
const props = defineProps<{
  fileId: string;
  fileName: string;
}>();

const { dialogRef, onDialogHide } = useDialogPluginComponent();
const { download } = useFilesActions();
const { isPendingLocationDoc, handleNavigateToFolder } = useNavigateFolder();

const downloadFile = () => {
  download(props.fileId, props.fileName);
};

const navigateToFolder = () => {
  handleNavigateToFolder(props.fileId);
};
</script>

<template>
  <q-dialog
    ref="dialogRef"
    full-height
    class="view-file-dialog-wrapper"
  >
    <div class="view-file-dialog row items-center justify-center">
      <q-card class="view-file-dialog__content">
        <q-card-section>
          <div class="row items-center no-wrap full-width view-file-section-header">
            <div class="col row items-center q-gutter-xs file-name-container">
              <div
                class="text-left overflow-hidden ellipsis text-subtitle2 cursor-pointer"
                style="max-width: 300px;"
                @click="copyToClipboardWithNotification(props.fileName)"
              >
                {{ props.fileName }}
                <q-tooltip class="text-body2">
                  {{ props.fileName }}
                </q-tooltip>
              </div>
              <q-btn
                round
                flat
                size="sm"
                icon="content_copy"
                color="primary"
                @click="copyToClipboardWithNotification(props.fileName)"
              >
                <q-tooltip class="text-body2">
                  Copy name
                </q-tooltip>
              </q-btn>
            </div>
            <div class="row items-center col-auto">
              <div class="q-ml-md">
                <q-btn
                  flat
                  round
                  size="md"
                  color="primary"
                  icon="download"
                  @click="downloadFile"
                >
                  <q-tooltip class="text-body2">
                    Download
                  </q-tooltip>
                </q-btn>
                <q-btn
                  flat
                  round
                  size="md"
                  color="primary"
                  icon="open_in_new"
                  :loading="isPendingLocationDoc"
                  :disable="isPendingLocationDoc"
                  @click="navigateToFolder"
                >
                  <q-tooltip class="text-body2">
                    Show in folder
                  </q-tooltip>
                </q-btn>
                <q-btn
                  flat
                  round
                  dense
                  size="md"
                  color="primary"
                  icon="close"
                  class="q-ml-md"
                  @click="onDialogHide"
                />
              </div>
            </div>
          </div>
        </q-card-section>

        <q-card-section class="q-pt-none view-file-dialog__section_body">
          <FileViewer
            :file-id="props.fileId"
            :file-name="props.fileName"
          />
        </q-card-section>
      </q-card>
    </div>
  </q-dialog>
</template>

<style lang="scss">
.view-file-dialog-wrapper {
  overflow: hidden;
  width: 100%;
  height: 100%;
}
.view-file-dialog {
  width: 100%;
  height: 100%;

  &__content {
    width: 100%;
    height: 100%;
  }

  &__section_body {
    height: calc(100% - 100px);
    overflow: hidden;
  }
}
</style>

<style scoped lang="scss">
.view-file-section-header {
  height: 44px;
}
.file-name-container {
  width: 400px;
}
.view-file-dialog__section_body {
  overflow: auto;
  max-height: calc(100% - 100px);
}
</style>
